import React, { useContext, useEffect, useRef, useState } from 'react';
import LaunchIcon from '@material-ui/icons/Launch';
import AppsIcon from '@material-ui/icons/Apps';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import { Navigation } from '../global/models/navigation-model';
import { useMapMaintenance, useMyCpgMaintenance } from '../global/hooks/useMaintenance';
import { UserContext } from '../global/models/app-context-model';
import { ApplicationList } from '../global/models/authenticated-model';
import { ExternalLink } from '../global/components/external-link-modal';
import { AuthSetting } from '../global/constants/auth-settings';
import { setAccessToken, setClientGuid } from '../global/ducks/authenticated-slice';
import { useDeserializedToken } from '../pages/arc/components/use-deserialized-token';
import { TcsmStates } from '../root/tcsm-model';
import { setUserContext } from '../global/ducks/app-context';
import { useBasePath } from '../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../pages/account-info/constants/routes';
import { beginLogout } from '../global/helpers/logout-state';
import { resetOdpOnLogout } from '../global/helpers/odp-helper';
import { CicAppContext, CicAppKeys } from './cic-nav-context';

const SecuritySettings = 'SecuritySettings';
const myCPG = 'MyCPG';
const map = 'MAP';
const customer360 = 'Customer360';
interface Props {
	navigation: Navigation,
}


export const CpgAppSelect = (props: Props) => {

	const container = useRef(null);
	const appContext = useContext(CicAppContext);
	const { logout } = useContext(AuthContext) as any;
	const [ariaExpand, setAriaExpand] = useState(false);
	const [ariaHidden, setAriaHidden] = useState(true);
	const token = useDeserializedToken();
	const dispatch = useDispatch();
	const history = useHistory();
	const applicationList = useSelector<TcsmStates, ApplicationList[]>(state => state.authenticatedState?.applicationList);
	const securityLink = applicationList?.filter(a => a.app_id === SecuritySettings);
	const otherLinks = applicationList?.filter(a => a.app_id !== SecuritySettings && a.app_id !== myCPG && a.app_id !== map && a.app_id !== customer360);
	const hasMap = applicationList?.find(a => a.app_id === map || a.app_id === customer360);
	const hasMyCpg = applicationList.filter(a => a.app_id === myCPG)?.length > 0;
	const mapMaintenance = useMapMaintenance();
	const myCpgMaintenance = useMyCpgMaintenance();

	const close = () => {
		setAriaExpand(false);
		setAriaHidden(true);
	};

	const open = () => {
		setAriaExpand(true);
		setAriaHidden(false);
	};

	const handleTriggerClick = () => {
		if (ariaExpand && !ariaHidden) {
			close();
		} else {
			open();
		}

	};

	const handleOutsideClick = (e: Event) => {
		if (!container?.current?.contains(e.target as HTMLElement)) {
			close();
		}
	};

	useEffect(() => {
		window.addEventListener('click', handleOutsideClick);
	}, [container]);

	const encodedReturnTo = encodeURIComponent(`${AuthSetting.REACT_APP_AUTH0_LOGOUT_URL}?returnTo=${window.location.origin}/services&client_id=${AuthSetting.REACT_APP_CLIENT_ID}`);
	const logoutUrl = `${AuthSetting.REACT_APP_CPG_LOGOUT_URL}?returnTo=${encodedReturnTo}`;

	const logoutApp = () => {
		// Flag the sign-out FIRST and synchronously: in TCSM this component renders inside the
		// forced-login gate, which would otherwise see isAuthenticated flip to false below and
		// redirect to Auth0 /authorize, cancelling our hosted-logout navigation.
		beginLogout();
		// Reset ODP: forget the reported login and clear the VUID cookie.
		resetOdpOnLogout();
		// Clear the back-end site-user cookie established by the auth bridge, then
		// continue the existing front-end logout regardless of the result.
		fetch('/api/auth/site-session', { method: 'DELETE', credentials: 'same-origin' })
			.catch(() => { /* non-fatal */ })
			.finally(async () => {
				dispatch(setAccessToken(null));
				dispatch(setClientGuid(null));
				// Clear the auth0-spa-js token cache (now persisted in localStorage) WITHOUT a
				// redirect, so the persisted token can't re-hydrate the session; then continue to
				// the hosted logout (which clears the Auth0 SSO + signin session).
				try { await logout({ openUrl: false }); } catch { /* non-fatal */ }
				window.location.replace(logoutUrl);
			});
	};

	const buttonOnClick = (context: UserContext, url: string) => {
		if (appContext === CicAppKeys.TCSM) {
			dispatch(setUserContext(context));
			close();
			history.push(url);
		} else {
			window.location.href = url;
		}
	};

	const myCpgPath = useBasePath(AccountInfoRoutes.MyCpgRoot);


	return (

		<div ref={container} className='nav-page-select'>
			<button type='button' className='nav-page-select-trigger' aria-expanded={ariaExpand} onClick={handleTriggerClick}>
				<svg className='account-icon' focusable='false'>
					<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#profile-icon' />
				</svg>
				{props.navigation.SignedInButtonText
					? props.navigation.SignedInButtonText.replace('{0}', token?.person?.fn || '')
					: token?.person?.fn}
				<svg className='arrow-icon' aria-hidden={ariaHidden} focusable='false'>
					<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#small-chevron-icon' />
				</svg>
			</button>

			<div className='nav-page-select-panel' aria-hidden={ariaHidden}>
				<p className='nav-page-select-panel-header'>
					<svg className='account-icon' focusable='false'>
						<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#profile-icon' />
					</svg>
					{props.navigation.ProfileText}
				</p>
				<ul>
					{
						securityLink && securityLink.length > 0 &&
						securityLink?.map(app =>
							<li key={app.app_id}>
								<ExternalLink
									linkUrl={app.landing_page}
									linkText={<>{app.app_name} <LaunchIcon /> </>}
									className='nav-page-select-link' />
							</li>,
						)
					}
				</ul>

				<ul className='nav-page-select-panel-inner'>
					<li><p className='nav-page-select-panel-header'><AppsIcon />{props.navigation.AppServicesText}</p></li>
					{(hasMyCpg && !myCpgMaintenance) && <li><button className='nav-page-select-link' type='button' onClick={() => buttonOnClick(UserContext.MY_CPG, myCpgPath)}>{props.navigation?.MyCpgLinkText}</button></li>}
					{(hasMap && !mapMaintenance) && <li> <button className='nav-page-select-link' type='button' onClick={() => buttonOnClick(hasMap?.app_id === customer360 ? UserContext.MINIMAP : UserContext.ARC, hasMap?.landing_page)}>{props.navigation.ArcLinkText}</button></li>}
					{
						otherLinks && otherLinks.length > 0 &&
						otherLinks?.map(app =>
							<li key={app.app_id}>
								<ExternalLink
									linkUrl={app.landing_page}
									linkText={<>{app.app_name} <LaunchIcon /> </>}
									className='nav-page-select-link' />
							</li>,
						)
					}
				</ul>
				<button className='nav-page-select-link sign-out' type='button' onClick={logoutApp}>{props.navigation?.LogoutText}
					<svg className='logout-icon' focusable='false'>
						<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#logout-icon' />
					</svg></button>
			</div>
		</div >

	);
};
